/**
 * ChatTextArea - Rich text input with file attachments
 *
 * Features:
 * - Multi-line text input with auto-resize
 * - RTL/LTR text direction detection (Arabic)
 * - File attachments via click, drag-drop, or paste
 * - File upload progress and error handling
 * - Submit on Enter, new line on Shift+Enter
 * - Loading state with stop button
 * - Error and warning notifications
 *
 * @example
 * ```tsx
 * <ChatTextArea
 *   onSubmit={(text, attachmentIds) => console.log(text, attachmentIds)}
 *   onFileUploaded={(id, ctx) => analytics.track('file_uploaded', ctx)}
 *   maxFiles={10}
 *   maxFileSize={102400}
 * />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Clock,
  FilePlus,
  CornerDownLeft,
  TriangleAlert,
  X,
  StopCircle,
  AppWindow,
  Sparkles,
  Check,
  Image as ImageIcon,
  FileText,
} from 'lucide-react';
import RichTextEditor, { type RichTextEditorRef } from './RichTextEditor';
import { useVoiceDictation } from './useVoiceDictation';
import { VoiceControls } from './VoiceControls';
import { SlashToolMenu } from './SlashToolMenu';
import {
  COMPOSER_TOOLS,
  filterComposerTools,
  composerToolToForcedArtifact,
  type ComposerTool,
} from './composerTools';
import { cn } from '../../lib/utils';
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_MB,
  ALLOWED_ATTACHMENT_MIME_TYPES,
  ALLOWED_ATTACHMENT_ACCEPT_ATTR,
  ALLOWED_IMAGE_ACCEPT_ATTR,
  ALLOWED_DOC_ACCEPT_ATTR,
} from '@ideoz/core/utils/attachment-types';
import { Button } from '../atoms/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../atoms/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../atoms/dropdown-menu';
import FilePreview from './file-preview';
import type {
  ChatTextAreaProps,
  AttachedFile,
  UseFileAttachmentsHook,
  LLMModel,
} from '../../types/ChatTextArea.types';
import { PROTOTYPER_GENERATION_MODELS } from '@ideoz/core';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../atoms/tooltip';

// Re-export types for convenience
export type {
  ChatTextAreaProps,
  AttachedFile,
  UseFileAttachmentsHook,
  LLMModel,
} from '../../types/ChatTextArea.types';

const prototyperGenerationModelKey = (model: { modelProvider: string; modelChoice: string }) =>
  `${model.modelProvider}|${model.modelChoice}`;

/**
 * Composer "+" menu tool actions. Selecting one drops the tool in as the
 * active-tool chip (exactly like picking it from the slash `/` menu): the
 * founder can then add context and send, or send straight away. Nothing is
 * submitted on selection.
 */
// The "+" menu and the slash (`/`) menu share one catalog (`composerTools.ts`).
// Each prompt ends with "as an artifact" so the model reliably produces a
// deliverable; on submit the prompt is prepended to whatever context was typed.
const STRATEGY_TOOLS: ReadonlyArray<ComposerTool> = COMPOSER_TOOLS.filter(
  (t) => t.category === 'Strategy'
);
const EXPERIENCE_TOOLS: ReadonlyArray<ComposerTool> = COMPOSER_TOOLS.filter(
  (t) => t.category === 'Experience'
);
// The Build tool (Prototype). Selecting Generate Prototype from the "+" menu
// drops this in as the active-tool chip (same as `/prototype`), so the founder
// can add context before sending instead of firing the build immediately.
const PROTOTYPE_TOOL: ComposerTool | undefined = COMPOSER_TOOLS.find((t) => t.id === 'prototype');

// Models offered in the composer, in display order: Gemini, Claude, Kimi, GLM.
const CHAT_MODELS: ReadonlyArray<LLMModel> = ['Gemini', 'Claude', 'Kimi', 'Glm'];

/**
 * Display label per model — the dropdown shows the full marketing model name.
 * The underlying value (llm_family payload) is unchanged.
 */
const CHAT_MODEL_LABELS: Partial<Record<LLMModel, string>> = {
  Glm: 'GLM 5.2',
  Kimi: 'Kimi K3',
  Claude: 'Claude Sonnet 5',
  Gemini: 'Gemini 3.6-flash',
};
const modelLabel = (model: LLMModel): string => CHAT_MODEL_LABELS[model] ?? model;

// Chat box: no toolbar; only list formatting is allowed, so typing "1. " /
// "- " autofills an ordered / bullet list and anything else stays plain text.
const CHAT_FORMATS = ['list'];

// Decide the input direction from its content: RTL as soon as the first
// *strong* directional character is Arabic, LTR otherwise. Leading neutral
// characters (whitespace, digits, punctuation, symbols) are skipped so a
// message that starts with e.g. a space or a number still flips correctly.
const isArabicText = (text: string): boolean => {
  // Arabic Unicode ranges (base, supplement, extended-A, presentation forms).
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  // Strong LTR: Latin, plus common European alphabets.
  const latinRegex = /[A-Za-z\u00C0-\u024F]/;

  for (const ch of text) {
    if (arabicRegex.test(ch)) return true; // first strong char is RTL
    if (latinRegex.test(ch)) return false; // first strong char is LTR
  }
  return false; // no strong character yet \u2192 default LTR
};

// Props with platform-specific hook injected
export interface ChatTextAreaInternalProps extends ChatTextAreaProps {
  /**
   * File attachments hook (provided by @ideoz/web).
   */
  useFileAttachmentsHook: (options: {
    maxFiles: number;
    maxFileSize: number;
    maxTotalSize: number;
    allowedFileTypes: string[];
    conversationId?: string | null;
    llmFamily?: string;
    onEnsureConversationId?: () => Promise<string | undefined>;
    onUploadComplete?: (attachmentId: string) => void;
    onUploadError?: (error: string) => void;
  }) => UseFileAttachmentsHook;
}

const ChatTextAreaInternal: React.FC<ChatTextAreaInternalProps> = ({
  variant = 'default',
  onSubmit,
  onCancel,
  placeholder = 'Type your message...',
  disabled = false,
  className = '',
  rows = 2,
  maxFiles = 10,
  // Same per-file limit + allowed types as every upload dialog — single
  // source of truth in @ideoz/core/utils/attachment-types.
  maxFileSize = MAX_ATTACHMENT_SIZE_BYTES,
  maxTotalSize = MAX_ATTACHMENT_SIZE_BYTES * 10,
  allowedFileTypes = [...ALLOWED_ATTACHMENT_MIME_TYPES],
  isChatLoading = false,
  isWeb = true,
  onFileUploaded,
  onLargeFilesDetected,
  onUnsupportedFilesDetected,
  analyticsContext,
  initialLlmFamily = 'Gemini',
  onLlmFamilyChange,
  conversationId,
  speechLang,
  onEnsureConversationId,
  useFileAttachmentsHook,
  isPrototypeOpen,
  isPrototypeCreating,
  currentMode,
  onGeneratePrototype,
  prototypeProductTypes = [],
  onBackToUncover,
  hasPrototypeCredits = true,
  hasProject = false,
  initiatedFromUncover = false,
  prototyperGenerationModel,
  onPrototyperGenerationModelChange,
  draftRequest,
}) => {
  const { t } = useTranslation();
  // Display label/description for a composer tool — translated by id, English fallback.
  const toolLabel = (tool: ComposerTool) =>
    t(`composerTool.${tool.id}.label`, { defaultValue: tool.label });
  // The founder's selected product types. Picking one from the Generate Prototype submenu both
  // starts that build and becomes the target for the next one, so a founder who chose Mobile App
  // does not silently get a web app the next time they type a brief and press Enter. Falls back to
  // the first option, including when the design system stops offering whatever was chosen.
  const [chosenProductType, setChosenProductType] = useState<string | undefined>(undefined);
  const activeProductType = prototypeProductTypes.some((pt) => pt.value === chosenProductType)
    ? chosenProductType
    : prototypeProductTypes[0]?.value;
  const [text, setText] = useState('');
  const [textDirection, setTextDirection] = useState<'ltr' | 'rtl'>('ltr');
  // Slash (`/`) command menu + the tool it selects. When `activeTool` is set a chip
  // shows above the editor and, on submit, the tool's prompt is prepended to the
  // typed message (see `handleSubmit`). The menu opens only while the editor content
  // starts with "/" and no tool is active yet.
  const [activeTool, setActiveTool] = useState<ComposerTool | null>(null);
  // Chip/placeholder text for the active tool. For Prototype, prefix the chosen
  // product type (e.g. "Mobile App Prototype") so the founder sees which build the
  // dropdown selection will produce; other tools show their own label.
  const activeToolLabel = activeTool
    ? activeTool.id === 'prototype'
      ? `${prototypeProductTypes.find((pt) => pt.value === activeProductType)?.label ?? ''} ${toolLabel(
          activeTool
        )}`.trim()
      : toolLabel(activeTool)
    : '';
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const slashMatches = useMemo(
    () => (slashOpen ? filterComposerTools(slashQuery) : []),
    [slashOpen, slashQuery]
  );
  // Wraps the menu + editor; used to detect clicks outside the composer so the
  // open menu can be dismissed.
  const slashAnchorRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // State for error details
  const [errorDetails, setErrorDetails] = useState<{
    summary: string;
    files: string[];
  } | null>(null);
  const [showErrorBar, setShowErrorBar] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showWarningBar, setShowWarningBar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  // Default to 'Gemini'
  // Default to Gemini when the conversation has no stored model.
  const normalizedInitialLlmFamily: LLMModel = initialLlmFamily || 'Gemini';

  const [selectedModel, setSelectedModel] = useState<LLMModel>(normalizedInitialLlmFamily);
  const prototyperSelectValue = useMemo(() => {
    if (!prototyperGenerationModel) {
      return prototyperGenerationModelKey(PROTOTYPER_GENERATION_MODELS[0]);
    }
    const found = PROTOTYPER_GENERATION_MODELS.find(
      (m) =>
        m.modelProvider === prototyperGenerationModel.modelProvider &&
        m.modelChoice === prototyperGenerationModel.modelChoice
    );
    return prototyperGenerationModelKey(found ?? PROTOTYPER_GENERATION_MODELS[0]);
  }, [prototyperGenerationModel]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<RichTextEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use file attachments hook (injected from platform)
  const { attachments, isUploading, addFiles, removeFile, clearAttachments, getAttachmentIds } =
    useFileAttachmentsHook({
      maxFiles,
      maxFileSize,
      maxTotalSize,
      allowedFileTypes,
      conversationId: conversationId ?? undefined,
      llmFamily: selectedModel,
      onEnsureConversationId,
      onUploadComplete: (attachmentId) => {
        console.log('File uploaded successfully:', attachmentId);
        // Determine location context
        let chatLocation = 'landing page chat';
        if (analyticsContext?.projectId) {
          chatLocation = 'project chat';
        } else if (analyticsContext?.isConversation) {
          chatLocation = 'conversation chat';
        }
        // Analytics: file.uploaded tracked via Redux middleware

        // Fire callback for analytics tracking
        onFileUploaded?.(attachmentId, {
          location: chatLocation,
          projectId: analyticsContext?.projectId,
        });
      },
      onUploadError: (error) => {
        // Parse error to extract file details if available
        let summary = error;
        let files: string[] = [];

        // Pattern 1: Files exceeding size limit
        // "10 files exceeding 100KB and cannot be uploaded. Please compress them, then try again: file1.pdf, file2.pdf, ..."
        const sizeMatch = error.match(/(\d+)\s+files?\s+exceeding\s+(\d+)KB.*?try again:\s*(.*)/);
        if (sizeMatch) {
          const [, fileCount, sizeLimit, fileList] = sizeMatch;
          summary = `${fileCount} files exceeding ${sizeLimit}KB and cannot be uploaded. Please compress them, then try again`;
          files = fileList
            .split(',')
            .map((f) => f.trim())
            .filter((f) => f);
        } else {
          // Pattern 2: Files with unsupported formats
          // "3 files with unsupported formats cannot be uploaded. Allowed types: ...: file1.exe, file2.bat, ..."
          const formatMatch = error.match(
            /(\d+)\s+files?\s+with\s+unsupported\s+formats?.*?Images:\s*(.*)/
          );
          if (formatMatch) {
            const [, , fileList] = formatMatch;
            const allowedPart = error.substring(0, error.lastIndexOf(':'));
            summary = allowedPart;
            files = fileList
              .split(',')
              .map((f) => f.trim())
              .filter((f) => f);
          } else {
            // Pattern 3: Maximum files limit
            // "Maximum 10 files allowed. 5 files cannot be added: file11.pdf, file12.pdf, ..."
            const limitMatch = error.match(
              /Maximum\s+(\d+)\s+files\s+allowed\.\s+(\d+)\s+files?\s+cannot\s+be\s+added:\s*(.*)/
            );
            if (limitMatch) {
              const [, maxFiles, excessCount, fileList] = limitMatch;
              summary = `Maximum ${maxFiles} files allowed. ${excessCount} files cannot be added`;
              files = fileList
                .split(',')
                .map((f) => f.trim())
                .filter((f) => f);
            } else {
              // Pattern 4: Single file upload errors
              // "Failed to upload "document.pdf": Server error"
              const uploadMatch = error.match(/Failed to upload "(.*?)":\s*(.*)/);
              if (uploadMatch) {
                const [, fileName, errorMsg] = uploadMatch;
                summary = `Failed to upload file: ${errorMsg}`;
                files = [fileName];
              } else {
                // Fallback: try to extract any filenames with extensions from the error
                const filePattern =
                  /[^\s,]+\.(pdf|doc|docx|txt|md|ppt|pptx|xls|xlsx|jpg|jpeg|png|gif|webp)/gi;
                const foundFiles = error.match(filePattern) || [];
                if (foundFiles.length > 0) {
                  files = foundFiles;
                  // Remove file names from summary to avoid duplication
                  summary = error
                    .replace(
                      new RegExp(
                        foundFiles.map((f) => f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
                        'gi'
                      ),
                      ''
                    )
                    .replace(/:\s*$/, '')
                    .trim();
                }
              }
            }
          }
        }

        setErrorDetails({
          summary,
          files,
        });
      },
    });

  // The rich editor auto-grows on its own; no manual textarea resize needed.

  // Sync selectedModel with initialLlmFamily prop (default Gemini).
  useEffect(() => {
    const normalized = initialLlmFamily || 'Gemini';
    if (normalized !== selectedModel) {
      setSelectedModel(normalized);
    }
  }, [initialLlmFamily, selectedModel]);

  // Asked from outside (the editor's Ask Ideoz). Any text comes in as a draft to finish; with
  // none, the composer only takes the cursor, and the founder says what they want in their words.
  useEffect(() => {
    if (!draftRequest) return;
    if (draftRequest.text) {
      setText(draftRequest.text);
      editorRef.current?.setContent(draftRequest.text);
    }
    editorRef.current?.focus?.();
  }, [draftRequest?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle markdown change from the rich editor, with direction detection.
  const handleTextChange = (newText: string) => {
    setText(newText);

    // Slash-command trigger: only while no tool is active and the whole input
    // starts with "/" (so a "/" mid-message never hijacks typing). The text after
    // "/" filters the tool list. A trailing newline closes it (the user moved on).
    if (!activeTool && newText.startsWith('/') && !newText.includes('\n')) {
      setSlashOpen(true);
      setSlashQuery(newText.slice(1));
      setSlashIndex(0);
    } else if (slashOpen) {
      setSlashOpen(false);
    }

    // Detect text direction based on content
    if (newText.trim()) {
      const isArabic = isArabicText(newText);
      setTextDirection(isArabic ? 'rtl' : 'ltr');
    } else {
      // Reset to LTR when text is empty
      setTextDirection('ltr');
    }
  };

  // Voice input (speech-to-text) — the hook owns the language choice, the
  // begin/confirm/cancel flow, and dictating live into the editor. `onRestore`
  // re-syncs direction/state after a cancel restores the input.
  const voice = useVoiceDictation({ editorRef, speechLang, onRestore: handleTextChange });

  // Pick a tool from the slash menu: clear the "/query" from the editor, show the
  // tool chip, and hand focus back so the user can type their message.
  const selectTool = (tool: ComposerTool) => {
    setActiveTool(tool);
    setSlashOpen(false);
    setSlashQuery('');
    setText('');
    editorRef.current?.clear();
    editorRef.current?.focus?.();
  };

  const clearActiveTool = () => setActiveTool(null);

  // Reopen the menu when the user clicks/focuses back into the composer and the
  // content still starts with "/" (an outside click had closed it). No-op when a
  // tool is already active or the menu is already open.
  const maybeReopenSlash = () => {
    if (slashOpen || activeTool) return;
    const content = editorRef.current?.getContent() ?? text;
    if (content.startsWith('/') && !content.includes('\n')) {
      setSlashQuery(content.slice(1));
      setSlashIndex(0);
      setSlashOpen(true);
    }
  };

  // Dismiss the open menu on a click outside the composer (the menu + editor live
  // inside `slashAnchorRef`; the menu suppresses its own mousedown so selecting a
  // tool doesn't count as "outside").
  useEffect(() => {
    if (!slashOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (slashAnchorRef.current && !slashAnchorRef.current.contains(e.target as Node)) {
        setSlashOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [slashOpen]);

  // Convert platform attachments to AttachedFile for FilePreview component
  const convertToAttachedFiles = (
    platformAttachments: UseFileAttachmentsHook['attachments']
  ): AttachedFile[] => {
    return platformAttachments.map((att) => ({
      id: att.id,
      file: att.file,
      preview: att.preview,
      isImage: att.isImage,
      isUploading: att.isUploading,
      uploadProgress: att.uploadProgress,
      error: att.error,
    }));
  };

  // Handle file uploads and show appropriate messages
  const handleAddFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      // Log any files larger than the shared limit (does not block upload).
      const largeFiles = fileArray.filter((f) => f.size > MAX_ATTACHMENT_SIZE_BYTES);
      if (largeFiles.length > 0) {
        // Fire callback for analytics tracking
        onLargeFilesDetected?.(largeFiles.length, {
          location: analyticsContext?.isConversation ? 'conversation chat' : 'landing page chat',
        });
      }

      // Check for unsupported files and show warning if needed
      const unsupportedFiles = fileArray.filter((file) => !allowedFileTypes.includes(file.type));

      if (unsupportedFiles.length > 0) {
        const unsupportedTypes = [...new Set(unsupportedFiles.map((f) => f.type))];
        setWarningMessage(`Unsupported file types: ${unsupportedTypes.join(', ')}`);

        // Fire callback for analytics tracking
        onUnsupportedFilesDetected?.(unsupportedFiles.length, unsupportedTypes, {
          location: analyticsContext?.isConversation ? 'conversation chat' : 'landing page chat',
        });
      }

      // Filter to only supported files
      const supportedFiles = fileArray.filter((file) => allowedFileTypes.includes(file.type));

      if (supportedFiles.length > 0) {
        try {
          await addFiles(supportedFiles);
        } catch (error) {
          console.error('Error adding files:', error);
        }
      }
    },
    [addFiles, allowedFileTypes, onLargeFilesDetected, onUnsupportedFilesDetected, analyticsContext]
  );

  // Handle file removal
  const handleRemoveFile = useCallback(
    async (fileId: string) => {
      // Guard against duplicate clicks while removal is in flight
      if (deletingIds.has(fileId)) return;
      setDeletingIds((prev) => new Set(prev).add(fileId));
      try {
        await removeFile(fileId);
        // Clear error and warning messages when files are removed
        setErrorDetails(null);
        setWarningMessage('');
      } catch (error) {
        // Swallow duplicate/not-found errors silently to avoid noisy UI
        console.error('Error removing file:', error);
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    },
    [removeFile, deletingIds]
  );

  // Auto-hide error message after 5 seconds and handle animation
  useEffect(() => {
    if (errorDetails) {
      setShowErrorBar(true);
      const timer = setTimeout(() => {
        setShowErrorBar(false);
        setTimeout(() => setErrorDetails(null), 300); // Wait for slide-out animation
      }, 6000);
      return () => clearTimeout(timer);
    } else {
      setShowErrorBar(false);
      return undefined;
    }
  }, [errorDetails]);

  // Auto-hide warning message after 5 seconds and handle animation
  useEffect(() => {
    if (warningMessage) {
      setShowWarningBar(true);
      const timer = setTimeout(() => {
        setShowWarningBar(false);
        setTimeout(() => setWarningMessage(''), 300); // Wait for slide-out animation
      }, 6000);
      return () => clearTimeout(timer);
    } else {
      setShowWarningBar(false);
      return undefined;
    }
  }, [warningMessage]);

  // Handle paste events
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        await handleAddFiles(files);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleAddFiles]);

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        await handleAddFiles(files);
      }
    },
    [handleAddFiles]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: Create new line (default behavior)
        return;
      } else {
        // Enter: Submit
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    // Enter while the slash menu is open selects the highlighted tool instead of
    // sending — the user is picking a command, not submitting a message.
    if (slashOpen) {
      const picked = slashMatches[slashIndex] ?? slashMatches[0];
      if (picked) selectTool(picked);
      return;
    }

    // Read the freshest markdown straight from the editor (state can lag a
    // keystroke behind when submitting via Enter).
    const typed = editorRef.current?.getContent() ?? text;
    // With a tool selected, send its prompt followed by the typed context; the
    // typed part is optional (the prompt alone is a valid request).
    const message = activeTool
      ? `${activeTool.prompt}${typed.trim() ? `\n\n${typed.trim()}` : ''}`
      : typed;
    if ((!message.trim() && attachments.length === 0) || isUploading) return;

    // Prototype must share the Generate Prototype button path (credit check +
    // prototyper mode + forced_artifact). A plain onSubmit would let the backend
    // fall back to inference. Pass only the typed context: `onGeneratePrototype`
    // owns the "Generate a <product type> prototype" instruction (built from
    // `activeProductType`), so the product-type phrase is never lost.
    if (activeTool?.id === 'prototype' && onGeneratePrototype) {
      if (disabled) return;
      if (voice.isListening) voice.confirm();
      onGeneratePrototype(typed.trim() || undefined, getAttachmentIds(), activeProductType);
      setText('');
      editorRef.current?.clear();
      clearActiveTool();
      clearAttachments();
      return;
    }

    if (!onSubmit) return;
    // Sending ends any in-progress dictation (keeps the transcript).
    if (voice.isListening) voice.confirm();
    setIsSubmitting(true);
    setTextDirection('ltr');
    try {
      const attachmentIds = getAttachmentIds();
      const forcedArtifact = activeTool ? composerToolToForcedArtifact(activeTool) : undefined;
      onSubmit(message, attachmentIds, selectedModel, conversationId ?? undefined, forcedArtifact);

      setText('');
      editorRef.current?.clear();
      clearActiveTool();
      clearAttachments();
    } catch (error) {
      console.error('Error submitting message:', error);
      setErrorDetails({
        summary: t('composer.submitFailed'),
        files: [],
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // Slash-menu keyboard nav. Runs in capture on the editor wrapper so it beats
  // Quill: Arrow keys move the highlight, Enter/Tab pick, Escape closes — none of
  // which reach the editor while the menu is open.
  const handleSlashKeyCapture = (e: React.KeyboardEvent) => {
    if (!slashOpen) {
      // Backspace on an empty editor removes the active tool chip — the natural
      // way to "delete" a selected tool, mirroring how a leading token is erased.
      if (
        e.key === 'Backspace' &&
        activeTool &&
        !(editorRef.current?.getContent() ?? text).trim()
      ) {
        e.preventDefault();
        e.stopPropagation();
        clearActiveTool();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setSlashIndex((i) => Math.min(i + 1, Math.max(0, slashMatches.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setSlashIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const picked = slashMatches[slashIndex] ?? slashMatches[0];
      if (picked) selectTool(picked);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setSlashOpen(false);
    }
  };

  /**
   * Open the native picker scoped to a given `accept` filter — lets the "+"
   * menu offer "Upload images" vs "Upload files" while reusing one input.
   * Falls back to the full allow-list when no filter is given.
   */
  const openFilePicker = (accept: string = ALLOWED_ATTACHMENT_ACCEPT_ATTR) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleAddFiles(files);
      // Clear input so same file can be selected again
      e.target.value = '';
    }
  };

  const containerClasses = variant === 'dimmed' ? 'opacity-60 bg-gray-50' : 'bg-transparent';

  // Prototype action for the "+" menu — same gating as the old standalone
  // button: only when in uncover/clarify (or a prototype was started from
  // uncover) with a project and prototype credits. In prototyper mode the
  // action becomes "Back to Uncover".
  const isBackToUncover = currentMode === 'prototyper';
  const showPrototypeAction =
    (currentMode === 'clarify' || initiatedFromUncover) && hasProject && hasPrototypeCredits;

  const dragOverlayClasses = isDragging
    ? 'absolute inset-0 bg-primary-50 border-2 border-dashed border-primary-400 rounded-2xl flex items-center justify-center z-10'
    : 'hidden h-0';

  return (
    <div className="relative w-full">
      {/* Error message bar with slide animation - absolutely positioned */}
      <div
        className={`absolute top-0 left-0 w-full z-20 overflow-hidden transition-all duration-300 ease-in-out ${
          showErrorBar && errorDetails
            ? 'max-h-32 opacity-100 -translate-y-[96%]'
            : 'max-h-0 opacity-0 -translate-y-full'
        }`}
      >
        <div className="p-3 bg-error-50 text-start flex flex-col gap-2 text-error-600 transform transition-all duration-300 ease-in-out">
          <div className="flex items-center gap-2">
            <TriangleAlert
              size={16}
              strokeWidth={2.5}
              color="#D92D20"
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <div className="text-xs font-normal">{errorDetails?.summary}</div>
            </div>
            <button
              onClick={() => {
                setShowErrorBar(false);
                setTimeout(() => setErrorDetails(null), 300);
              }}
              className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
              type="button"
            >
              <X size={20} strokeWidth={2.5} color="#B42318" />
            </button>
          </div>
          <div className="px-6 py-1">
            {errorDetails?.files && errorDetails.files.length > 0 && (
              <div className="text-xs leading-5 max-h-16 overflow-hidden">
                {(() => {
                  const files = errorDetails.files;
                  const maxChars = 335;
                  let displayFiles: string[] = [];
                  let currentLength = 0;

                  // Dynamically determine how many files to display based on character limit
                  for (let i = 0; i < files.length; i++) {
                    const fileWithComma = i === 0 ? files[i] : ', ' + files[i];
                    if (currentLength + fileWithComma.length <= maxChars) {
                      displayFiles.push(files[i]);
                      currentLength += fileWithComma.length;
                    } else {
                      break;
                    }
                  }

                  const remainingCount = files.length - displayFiles.length;

                  return (
                    <div className="break-words">
                      {displayFiles.map((file, index) => (
                        <span key={index}>
                          {file}
                          {index < displayFiles.length - 1 && ', '}
                        </span>
                      ))}
                      {remainingCount > 0 && (
                        <span className="">, + {remainingCount} other files</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warning message bar with slide animation - absolutely positioned */}
      <div
        className={`absolute top-0 left-0 w-full z-20 overflow-hidden transition-all duration-300 ease-in-out ${
          showWarningBar && warningMessage
            ? 'max-h-20 opacity-100 -translate-y-[96%]'
            : 'max-h-0 opacity-0 -translate-y-full'
        }`}
      >
        <div className="p-2 bg-yellow-50 border-l-4 border-l-yellow-400 border-t border-r border-yellow-200 rounded-t-lg flex gap-2 text-yellow-600 transform transition-all duration-300 ease-in-out">
          <Clock size={16} strokeWidth={2.5} color="#DC6803" />
          <span className="text-sm font-medium flex-1 text-start">{warningMessage}</span>
          <button
            onClick={() => {
              setShowWarningBar(false);
              setTimeout(() => setWarningMessage(''), 300);
            }}
            className="p-1 hover:bg-yellow-100 rounded transition-colors"
            type="button"
          >
            <X size={14} strokeWidth={2.5} color="#667085" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`relative flex flex-col w-full rounded-2xl p-4 justify-center items-end gap-2 focus-within:border-gray-300 dark:focus-within:border-gray-700 transition-all duration-500 ease-in-out border border-gray-200 dark:border-gray-800 min-h-[89px] ${containerClasses} ${className} ${isPrototypeOpen ? '!w-full' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        <div className={dragOverlayClasses}>
          <div className="text-center">
            <FilePlus size={26} strokeWidth={2.5} />
            <p className="text-sm font-medium text-primary-700">Drop files here to attach</p>
          </div>
        </div>

        {/* File attachments preview */}
        <FilePreview
          files={convertToAttachedFiles(attachments)}
          onDelete={handleRemoveFile}
          hasDelete={true}
        />

        {/* WYSIWYG input (markdown in/out). Quill's toolbar sits above the
            editor and is toggled by the "A" button in the bottom bar. */}
        <div
          ref={slashAnchorRef}
          className="relative w-full"
          dir={textDirection}
          onKeyDownCapture={handleSlashKeyCapture}
          // Clicking/focusing back into the composer while a "/…" is still typed
          // reopens the menu that an outside click had closed.
          onClick={maybeReopenSlash}
          onFocusCapture={maybeReopenSlash}
        >
          {/* Slash (`/`) tool menu — opens above the input while typing "/…". */}
          {slashOpen && (
            <SlashToolMenu
              className="absolute bottom-full left-0 mb-2"
              tools={slashMatches}
              activeId={slashMatches[slashIndex]?.id ?? null}
              onSelect={selectTool}
              onHover={(id) =>
                setSlashIndex(
                  Math.max(
                    0,
                    slashMatches.findIndex((t) => t.id === id)
                  )
                )
              }
            />
          )}

          {/* Active tool chip — on its own line above the text. Its prompt is
              prepended to the typed message on submit. */}
          {activeTool && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-1 rounded-full py-1 pe-1 ps-2.5 text-xs font-medium border-blue-300 bg-blue-50 text-blue-500 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300">
                <Sparkles size={12} />
                {activeToolLabel}
                <button
                  type="button"
                  aria-label={t('composer.removeTool', { tool: activeToolLabel })}
                  onClick={clearActiveTool}
                  data-testid="active-tool-clear"
                  className="ms-0.5 rounded-full p-0.5 hover:bg-primary-100 dark:hover:bg-primary-500/25"
                >
                  <X size={12} />
                </button>
              </span>
            </div>
          )}

          <RichTextEditor
            ref={editorRef}
            initialValue=""
            placeholder={
              voice.isListening
                ? t('composer.listening')
                : activeTool
                  ? t('composer.addContext', { tool: activeToolLabel })
                  : placeholder
            }
            dir={textDirection}
            onChange={handleTextChange}
            onEnterSubmit={handleSubmit}
            // No toolbar/button — lists form live as you type ("1. ", "- ")
            // via Quill's markdown-shortcut autofill.
            toolbar={false}
            formats={CHAT_FORMATS}
            className={cn(
              '[&_.ql-container]:!h-auto [&_.ql-editor]:!h-auto',
              '[&_.quill-editor-container]:!border-0 [&_.quill-editor-container]:p-0 [&_.quill-editor-container]:focus-within:!outline-0',
              '[&_.ql-editor]:p-0 [&_.ql-editor]:min-h-6 [&_.ql-editor]:max-h-[120px] [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:scrollbar-chat [&_.ql-editor]:leading-5',
              '[&_.ql-editor.ql-blank::before]:not-italic [&_.ql-editor.ql-blank::before]:text-gray-300 [&_.ql-editor.ql-blank::before]:left-0',
              '[&_.ql-toolbar]:bg-transparent [&_.ql-toolbar]:p-0 [&_.ql-toolbar]:mb-4'
            )}
          />
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          accept={ALLOWED_ATTACHMENT_ACCEPT_ATTR}
          data-testid="chat-file-input"
        />

        {/* Bottom bar */}
        <div className="flex justify-end items-end self-stretch pe-1 relative top-2 -mx-1">
          {/* Right div - Action buttons */}
          <div className="flex items-center">
            {/* "+" menu (uploads / tools) — hidden while dictating. Model
                selection is intentionally omitted: all chats use Gemini. */}
            {!voice.isListening && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="tertiary-gray"
                    size="sm"
                    disabled={disabled}
                    className="p-1 w-8 h-8 hover:bg-blue-light-50 hover:text-blue-600 disabled:opacity-50"
                    data-testid="chat-add-files-button"
                  >
                    <Plus size={18} strokeWidth={2} stroke="currentColor" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-60">
                  {/* Uploads — split by type, both reuse the one hidden input */}
                  <DropdownMenuItem
                    className="gap-2"
                    onSelect={() => openFilePicker(ALLOWED_IMAGE_ACCEPT_ATTR)}
                    data-testid="menu-upload-images"
                  >
                    <ImageIcon size={16} /> {t('composer.uploadImages')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2"
                    onSelect={() => openFilePicker(ALLOWED_DOC_ACCEPT_ATTR)}
                    data-testid="menu-upload-files"
                  >
                    <FileText size={16} /> {t('composer.uploadFiles')}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  {/* Strategy tools — each drops in as the active-tool chip so the
                      founder can add context before sending (same as `/tool`). */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="menu-strategy-tools">
                      {t('composer.strategyTools')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {STRATEGY_TOOLS.map((tool) => (
                        <DropdownMenuItem
                          key={tool.label}
                          disabled={disabled}
                          onSelect={() => selectTool(tool)}
                          data-testid={`menu-strategy-${tool.label}`}
                        >
                          {toolLabel(tool)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  <DropdownMenuSeparator />

                  {/* Experience tools — each drops in as the active-tool chip so the
                      founder can add context before sending (same as `/tool`). */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="menu-experience-tools">
                      {t('composer.experienceTools')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {EXPERIENCE_TOOLS.map((tool) => (
                        <DropdownMenuItem
                          key={tool.label}
                          disabled={disabled}
                          onSelect={() => selectTool(tool)}
                          data-testid={`menu-experience-${tool.label}`}
                        >
                          {toolLabel(tool)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  {showPrototypeAction && (
                    <>
                      <DropdownMenuSeparator />
                      {isBackToUncover ? (
                        <DropdownMenuItem
                          className="gap-2"
                          disabled={isChatLoading || isPrototypeCreating || disabled}
                          onSelect={onBackToUncover}
                          data-testid="menu-back-to-uncover"
                        >
                          <Sparkles size={16} />
                          {t('composer.backToUncover')}
                        </DropdownMenuItem>
                      ) : prototypeProductTypes.length >= 2 ? (
                        // Multiple product types selected → offer them as a submenu, ticking the
                        // one the next build will use (the last one picked, else the first).
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger
                            disabled={isChatLoading || isPrototypeCreating || disabled}
                            className="gap-2"
                            data-testid="menu-generate-prototype"
                          >
                            <AppWindow size={16} />
                            {t('composer.generatePrototype')}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {prototypeProductTypes.map((pt) => (
                              <DropdownMenuItem
                                key={pt.value}
                                className="gap-2"
                                disabled={isChatLoading || isPrototypeCreating || disabled}
                                onSelect={() => {
                                  // Pin the chosen type, then drop the Prototype chip
                                  // in so the founder can add context before sending
                                  // (handleSubmit reads `activeProductType`).
                                  setChosenProductType(pt.value);
                                  if (PROTOTYPE_TOOL) selectTool(PROTOTYPE_TOOL);
                                }}
                                data-testid={`menu-generate-prototype-${pt.value}`}
                              >
                                {pt.label}
                                {pt.value === activeProductType && (
                                  <Check size={14} className="ms-auto" />
                                )}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      ) : (
                        <DropdownMenuItem
                          className="gap-2"
                          disabled={isChatLoading || isPrototypeCreating || disabled}
                          // Drop the Prototype chip in (same as `/prototype`) so the
                          // founder can add context before the build starts; submit
                          // routes through onGeneratePrototype with activeProductType.
                          onSelect={() => {
                            if (PROTOTYPE_TOOL) selectTool(PROTOTYPE_TOOL);
                          }}
                          data-testid="menu-generate-prototype"
                        >
                          <AppWindow size={16} />
                          {t('composer.generatePrototype')}
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* While dictating, the mic is replaced by the listening controls. */}
            {voice.isListening && <VoiceControls.Listening voice={voice} />}

            {/* Mic + language dropdown — always available (except while dictating
                or generating), so the user can dictate again after confirming. */}
            {!voice.isListening && !isChatLoading && voice.isSupported && (
              <VoiceControls.Mic voice={voice} disabled={disabled} />
            )}

            {/* Stop while generating; otherwise the send button. Send stays
                visible in every state and is disabled while empty or recording. */}
            {isChatLoading ? (
              <Button
                variant="tertiary-gray"
                size="sm"
                onClick={handleCancel}
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900 p-0 text-white hover:bg-gray-800 disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                data-testid="chat-stop-button"
                data-track="stop_generation"
              >
                <StopCircle size={16} strokeWidth={2} />
              </Button>
            ) : (
              <Button
                variant="tertiary-gray"
                size="sm"
                onClick={handleSubmit}
                disabled={
                  disabled ||
                  isSubmitting ||
                  isUploading ||
                  voice.isListening ||
                  // An active tool chip is itself a valid request (its prompt is
                  // sent), so the founder can send straight after picking a tool
                  // without typing anything.
                  (!text.trim() && attachments.length === 0 && !activeTool)
                }
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900 p-0 text-white hover:bg-gray-800 disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                data-testid="chat-submit-button"
                data-track="send_message"
              >
                <CornerDownLeft size={16} strokeWidth={2} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* AI disclaimer — sits directly under the composer box. */}
      <p
        className="mt-2 text-center text-xs text-gray-400 dark:text-gray-500"
        data-testid="composer-ai-disclaimer"
      >
        {t('composer.aiDisclaimer')}
      </p>
    </div>
  );
};

// Export the internal component for platform packages to wrap
export { ChatTextAreaInternal };

// Export as default too
export default ChatTextAreaInternal;
